// assets/b-track-contact.js (#796): one enquiry funnel on every store, sent to GA4 through the shared
// tracker (assets/b-track.js, #732: consent-gated, Shopify customer events + dataLayer). Loaded once per
// page by snippets/track-enquiry, after b-track.js.
//
//   1. cta_click      an enquiry button ([data-open-enquiry]) is clicked
//      contact_click  any tel: or mailto: link is clicked
//      chat_open      Gorgias chat opens (track:enquiry:chat)
//   2. enquiry_open   a form opens (track:contact:open: the pop-up, or an on-page form coming into view)
//   3. enquiry_start  the first field is focused, once per form open (track:contact:start)
//   4. enquiry_submit Send is pressed (track:contact:submit)
//   5. generate_lead  the page after a confirmed send (track:contact:posted): the one key event
//      enquiry_error  the send came back with an error, with error_type (track:contact:error)
//
// Every step carries lead_source (popup, contact_page, product_contact, chat, phone, email, or a store
// form's own, e.g. quote_cart), request_type, cta_surface (which button), machine_interest, page_type,
// lead_path (this visit's last 4 steps, oldest first) and, when the machine finder has run,
// finder_families and finder_days_since. Steps 3-5 carry the details of the form that opened. Only
// these fields are read, so the shopper's email on track:contact:submit never reaches an event.
//
// A store's own form joins the same funnel through window.bTrackLead(name, details, pathWord); the
// store-specific events (Koenig's cta_*) keep firing alongside until reports move over.

const STATE_KEY = 'b_lead'; // sessionStorage: path and the open enquiry's details, no shopper data
const FINDER_KEY = 'hb_finder'; // the machine finder's answers (Koenig's km-machine-finder.js)
const PATH_MAX = 4;
const ENQUIRY = ['lead_source', 'request_type', 'cta_surface', 'machine_interest'];
const PATH_WORD = { cta_click: 'cta_surface', contact_click: 'lead_source', chat_open: 'lead_source', enquiry_open: 'lead_source' };

export function addStep(path, word) {
  if (!word || path[path.length - 1] === word) return path;
  return [...path, word].slice(-PATH_MAX);
}

export function contactMethod(href) {
  const h = String(href || '').trim().toLowerCase();
  if (h.startsWith('tel:')) return 'phone';
  if (h.startsWith('mailto:')) return 'email';
  return '';
}

// Menus and footers by their family name, any other section by its key.
const plainSurface = (key) => (/footer/.test(key) ? 'footer' : /menu|nav|header/.test(key) ? 'menu' : key);

// shortcut: the section's key from its wrapper id (hero, footer-main), not its type file, because the
// storefront HTML doesn't carry the type; a store whose JSON keys are random ids reports those ids.
export function surfaceOf(node) {
  if (node.closest('[data-track-contact-modal]')) return 'popup';
  const section = node.closest('.shopify-section');
  if (section && section.id) return plainSurface(section.id.replace(/^shopify-section-/, '').replace(/^.*__/, ''));
  return '';
}

// The enquiry's Source line names its button: 'km-quote:card' -> card, 'menu-help' -> menu.
export function surfaceFromSource(source) {
  const s = String(source || '');
  if (s.includes(':')) return s.split(':').pop();
  return /footer|menu/.test(s) ? plainSurface(s) : '';
}

function read(store, key) {
  try { return JSON.parse(store.getItem(key)); } catch (error) { return null; }
}

function finder(run) {
  const at = run && Date.parse(run.completed_at);
  if (!at) return null;
  const families = run.recommendation && Array.isArray(run.recommendation.families) ? run.recommendation.families : [];
  return { at: run.completed_at, finder_families: families.join(','), finder_days_since: Math.max(0, Math.floor((Date.now() - at) / 86400000)) };
}

function pageType() {
  const site = document.querySelector('[data-track-contact-modal][data-page-type]');
  return site ? site.getAttribute('data-page-type') : '';
}

const filled = (params) => Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''));

export function leadStep(name, details, pathWord) {
  try {
    const state = read(sessionStorage, STATE_KEY) || {};
    let path = Array.isArray(state.path) ? state.path : [];
    let params = filled(details);

    // A finder run this visit goes into the path before the step it led to.
    const thisVisit = finder(read(sessionStorage, FINDER_KEY));
    if (thisVisit && thisVisit.at !== state.finderAt) {
      path = addStep(path, 'finder');
      state.finderAt = thisVisit.at;
    }
    let run = thisVisit;
    try { run = run || finder(read(localStorage, FINDER_KEY)); } catch (error) { /* no storage */ }

    if (name === 'cta_click') {
      state.button = params.cta_surface || '';
    } else if (name === 'enquiry_open' || name === 'chat_open') {
      // The button clicked just before belongs to the form or chat it opened; a button that sent no
      // cta_click of its own (a store's, named in the Source line) joins the path here.
      if (!state.button && params.cta_surface) path = addStep(path, params.cta_surface);
      params = { cta_surface: state.button, ...params };
      state.button = '';
      state.enquiry = filled(Object.fromEntries(ENQUIRY.map((k) => [k, params[k]])));
    } else if (name !== 'contact_click') {
      params = { ...state.enquiry, ...params };
    }
    path = addStep(path, pathWord || params[PATH_WORD[name]]);
    state.path = path;
    try { sessionStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch (error) { /* blocked: path starts over */ }

    params = filled({ ...params, lead_path: path.join(' > '), page_type: params.page_type || pageType(),
      finder_families: run && run.finder_families, finder_days_since: run && run.finder_days_since });
    if (typeof window.bTrack === 'function') window.bTrack(name, params);
    // A confirmed lead ends the journey: the next enquiry's path starts fresh (standards rule 3).
    if (name === 'generate_lead') {
      state.path = [];
      state.enquiry = {};
      state.button = '';
      try { sessionStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch (error) { /* blocked */ }
    }
  } catch (error) {
    // A tracker error never stops the page.
  }
}
window.bTrackLead = window.bTrackLead || leadStep;

// The shared pop-up and on-page forms (snippets/track-contact-modal).
function fromForm(d) {
  const product = d.pageType === 'product';
  return {
    lead_source: !d.inline ? 'popup' : product ? 'product_contact' : 'contact_page',
    request_type: d.requestType,
    cta_surface: surfaceFromSource(d.source),
    machine_interest: d.about || (product ? d.resource : ''),
    error_type: d.errorType,
    page_type: d.pageType,
  };
}

const FORM_STEPS = {
  'track:contact:open': 'enquiry_open',
  'track:contact:start': 'enquiry_start',
  'track:contact:submit': 'enquiry_submit',
  'track:contact:posted': 'generate_lead',
  'track:contact:error': 'enquiry_error',
};
for (const [from, to] of Object.entries(FORM_STEPS)) {
  document.addEventListener(from, (event) => leadStep(to, fromForm(event.detail || {})));
}

document.addEventListener('track:enquiry:chat', (event) => {
  const d = event.detail || {};
  leadStep('chat_open', { lead_source: 'chat', request_type: d.requestType, cta_surface: surfaceFromSource(d.source) });
});

// Capture phase, so a handler that stops the click (a drawer, the pop-up) can't hide it, and the
// button is remembered before the pop-up it opens reports enquiry_open.
document.addEventListener('click', (event) => {
  const target = event.target && event.target.closest ? event.target : null;
  if (!target) return;
  const button = target.closest('[data-open-enquiry]');
  if (button) {
    leadStep('cta_click', {
      request_type: button.getAttribute('data-enquiry-type') || 'message',
      cta_surface: surfaceFromSource(button.getAttribute('data-enquiry-source')) || surfaceOf(button),
      machine_interest: button.getAttribute('data-enquiry-subject'),
    });
    return;
  }
  const link = target.closest('a[href]');
  const method = link ? contactMethod(link.getAttribute('href')) : '';
  if (method) leadStep('contact_click', { lead_source: method, cta_surface: surfaceOf(link) });
}, true);
